import { ButtonBase, type SxProps, type Theme } from "@mui/material";
import { styled } from "@mui/material/styles";

import { type NumberFieldSize } from "ui/number-field/number-field.types";

import { borderRadius } from "design-tokens";

/** Figma's `Buttons` column, its left rule included: 28px on Large (`medium`), 24px on Medium (`small`). */
export const STEPPER_WIDTH: Record<NumberFieldSize, number> = { medium: 28, small: 24 };

/** `button/medium/label/icon` — the caret's box at both sizes. */
export const STEPPER_GLYPH_SIZE = 16;

/**
 * Every colour the field paints beyond the theme's `MuiOutlinedInput` entry. A map rather than inline
 * reads so the Storybook `Tokens` page shows the same values the field is painted with.
 */
export const NUMBER_FIELD_COLOR = {
  affix: (theme: Theme) => theme.palette.text.secondary,
  affixDisabled: (theme: Theme) => theme.palette.text.disabled,
  stepperRule: (theme: Theme) => theme.palette.border.secondary,
  glyph: (theme: Theme) => theme.palette.text.secondary,
  glyphDisabled: (theme: Theme) => theme.palette.text.disabled,
  buttonHover: (theme: Theme) => theme.palette.action.hover,
} satisfies Record<string, (theme: Theme) => string>;

/** The class on the spans round `prefix` and `suffix`, which the control colours. */
export const AFFIX_CLASS = "ApolloNumberField-affix";

/**
 * `sx` shorthands do NOT work inside `styled()` — every value below is an explicit `theme.*` lookup.
 *
 * The column sits 1px in from the control's top, right and bottom, so its rule runs between the
 * outline's two edges as Figma draws it, and the buttons' hover fill stops inside the border. The outline
 * itself is MUI's absolutely positioned `fieldset`, which takes no room, hence the margin.
 */
export const NumberFieldStepper = styled("span", {
  shouldForwardProp: (prop) => prop !== "stepperSize",
})<{ stepperSize: NumberFieldSize }>(({ theme, stepperSize }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "stretch",
  flexShrink: 0,
  boxSizing: "border-box",
  width: STEPPER_WIDTH[stepperSize],
  margin: "1px 1px 1px auto",
  // Size containment keeps the two 16px carets out of the control's height: stacked they are 32px, which
  // would push the 32px small control to 34. Stretched, the column takes whatever height the value sets.
  contain: "size",
  borderLeft: `1px solid ${NUMBER_FIELD_COLOR.stepperRule(theme)}`,
  // The control's corner less the border it sits inside.
  borderTopRightRadius: `calc(${borderRadius.md} - 1px)`,
  borderBottomRightRadius: `calc(${borderRadius.md} - 1px)`,
  overflow: "hidden",
}));

export const StepperButton = styled(ButtonBase)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  color: NUMBER_FIELD_COLOR.glyph(theme),
  "&:hover": { backgroundColor: NUMBER_FIELD_COLOR.buttonHover(theme) },
  "&.Mui-disabled": { color: NUMBER_FIELD_COLOR.glyphDisabled(theme) },
  "& > svg": { width: STEPPER_GLYPH_SIZE, height: STEPPER_GLYPH_SIZE },
}));

export const CONTROL_SX: SxProps<Theme> = (theme) => ({
  "&.MuiInputBase-adornedEnd": { paddingRight: 0 },
  "@supports (field-sizing: content)": {
    "& .MuiOutlinedInput-input": {
      fieldSizing: "content",
      width: "auto",
      minWidth: "1ch",
      flex: "0 1 auto",
    },
  },
  [`& .${AFFIX_CLASS}`]: { color: NUMBER_FIELD_COLOR.affix(theme) },
  [`&.Mui-disabled .${AFFIX_CLASS}`]: { color: NUMBER_FIELD_COLOR.affixDisabled(theme) },
});
